import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';

// ─── Client-user deliveries API ───────────────────────────────────────
// GET /client-user/v1/deliveries — paginated list of the client's deliveries.
// Mirrors next-portal-v2-web/infrastructure/api/delivery/delivery.ts.
// Client-user endpoints require the LINK channel + live mode headers
// (see CHANNEL_HEADERS in clientAuth.ts); the bearer token is attached by
// the shared axios interceptor.
const CHANNEL_HEADERS_LIVE = {
  'X-Channel': 'LINK',
  'X-Api-Mode': 'live',
} as const;

// All delivery statuses surfaced to the client (excludes CREATED/EXPIRED).
export const DELIVERY_STATUSES = [
  'PENDING_PAYMENT',
  'QUEUEING',
  'ALLOCATING',
  'PENDING_PICKUP',
  'PICKING_UP',
  'PENDING_DELIVERY',
  'IN_DELIVERY',
  'PREPARING',
  'VERIFIED',
  'NOT_VERIFIED',
  'COMPLETED',
  'FAILED',
  'CANCELLED',
  'PENDING_RETURN',
  'IN_RETURN',
  'RETURNED',
  'FAILED_IN_RETURN',
  'ON_HOLD',
  'DISPOSED',
] as const;

export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

export interface DeliveryCourier {
  id?: number;
  name?: string;
  phone?: string;
  imageUrl?: string;
}

export interface DeliveryParty {
  firstName?: string;
  lastName?: string;
  companyName?: string | null;
  phone?: string;
  email?: string;
}

export interface DeliveryCashOnDelivery {
  amount?: number;
  paymentMethod?: string | null;
}

export interface DeliverySchedule {
  pickupTimeFrom?: string;
  pickupTimeTo?: string;
}

export interface DeliveryProviderOutlet {
  id?: string;
  name?: string;
}

export interface DeliveryDocuments {
  hasShippingLabel?: boolean;
  hasShippingDoc?: boolean;
}

// A single row in the deliveries list response (the API returns a subset of
// the full delivery object — kept loose where the gateway is inconsistent).
export interface Delivery {
  deliveryID: string;
  providerOrderID?: string;
  status: DeliveryStatus | string;
  courier?: DeliveryCourier;
  sender?: DeliveryParty;
  recipient?: DeliveryParty;
  schedule?: DeliverySchedule;
  cashOnDelivery?: DeliveryCashOnDelivery | null;
  providerOutlet?: DeliveryProviderOutlet;
  documents?: DeliveryDocuments;
  quote?: Pick<DeliveryDetailQuote, 'origin' | 'destination'>;
  invoiceNo?: string | null;
  pickupAt?: string | null;
  completedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  pickup_sla_status?: 'ON_TIME' | 'LATE' | null;
  complete_sla_status?: 'ON_TIME' | 'LATE' | null;
}

export interface Pagination {
  total: number;
  page: number;
  size: number;
  lastPage: number;
}

export interface DeliveryListResponse {
  data: Delivery[];
  pagination: Pagination;
}

export interface DeliveryListParams {
  page?: number;
  size?: number;
  status?: string; // comma-separated DeliveryStatus values
  providerOutletIDs?: string; // comma-separated outlet IDs (Recipient)
  driverIDs?: string; // comma-separated driver IDs (Mitra)
  startDate?: string; // created date range
  endDate?: string;
  search?: string;
}

export const getDeliveries = async (
  params: DeliveryListParams = {}
): Promise<DeliveryListResponse> => {
  try {
    // Default payload sends only page/size/status (status empty = all). The
    // optional filters are omitted entirely unless they carry a real value.
    const query: Record<string, string | number> = {
      page: params.page ?? 1,
      size: params.size ?? 10,
      status: params.status ?? '',
    };
    if (params.providerOutletIDs)
      query.providerOutletIDs = params.providerOutletIDs;
    if (params.driverIDs) query.driverIDs = params.driverIDs;
    if (params.startDate) query.startDate = params.startDate;
    if (params.endDate) query.endDate = params.endDate;
    if (params.search) query.search = params.search;

    const response = await Axios.get<DeliveryListResponse>(
      '/client-user/v1/deliveries',
      {
        headers: CHANNEL_HEADERS_LIVE,
        params: query,
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

// The status-count buckets shown above the table. The summaries endpoint
// returns a flat { status: count } map keyed by these camelCase keys.
export interface DeliverySummary {
  allocating?: number;
  pickingUp?: number;
  inDelivery?: number;
  completed?: number;
  failed?: number;
  cancelled?: number;
  [key: string]: number | undefined;
}

export interface DeliverySummaryResponse {
  data: DeliverySummary;
}

export const getDeliverySummary = async (
  params: {
    startDate?: string;
    endDate?: string;
    providerOutletIDs?: string;
  } = {}
): Promise<DeliverySummary> => {
  try {
    const response = await Axios.get<DeliverySummaryResponse>(
      '/client-user/v1/deliveries/summaries',
      {
        headers: CHANNEL_HEADERS_LIVE,
        params: {
          startDate: params.startDate ?? '',
          endDate: params.endDate ?? '',
          providerOutletIDs: params.providerOutletIDs ?? '',
        },
      }
    );
    return response.data.data;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

// ─── Detail ───────────────────────────────────────────────────────────
export interface DeliveryDetailPackage {
  name?: string;
  description?: string;
  quantity?: number;
  weight?: number;
  price?: number;
}

export interface DeliveryDetailLocation {
  address?: string;
  notes?: string;
  coordinates?: { latitude?: number; longitude?: number };
}

export interface DeliveryTimelineEntry {
  status?: string;
  createdAt?: string;
  created_at?: string;
  notes?: string | null;
  media?: { url: string; type: string; createdAt: string }[];
}

export interface DeliveryDetailQuote {
  service?: { category?: string; type?: string; name?: string };
  currency?: { code?: string; symbol?: string };
  amount?: number;
  discountAmount?: number;
  finalAmount?: number;
  distance?: number;
  tax?: number;
  packages?: DeliveryDetailPackage[];
  origin?: DeliveryDetailLocation;
  destination?: DeliveryDetailLocation;
}

export interface DeliveryDetail {
  deliveryID: string;
  providerOrderID?: string;
  status: string;
  paymentMethod?: string;
  quote?: DeliveryDetailQuote;
  courier?: DeliveryCourier;
  media?: { url: string; type: string; createdAt: string }[];
  timeline?: DeliveryTimelineEntry[];
  sender?: DeliveryParty;
  recipient?: DeliveryParty;
  providerOutlet?: DeliveryProviderOutlet;
  cashOnDelivery?: DeliveryCashOnDelivery | null;
  schedule?: DeliverySchedule;
  trackingURL?: string;
  invoiceNo?: string | null;
  isCancellable?: boolean;
  pickupAt?: string | null;
  completedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  pickup_sla_status?: 'ON_TIME' | 'LATE' | null;
  complete_sla_status?: 'ON_TIME' | 'LATE' | null;
  max_pickup_at?: string | null;
  max_completion_at?: string | null;
}

export interface DeliveryDetailResponse {
  data: DeliveryDetail;
}

// GET /client-user/v1/deliveries/{deliveryID} — full delivery detail.
export const getDeliveryDetail = async (
  deliveryID: string
): Promise<DeliveryDetail> => {
  try {
    const response = await Axios.get<DeliveryDetailResponse>(
      `/client-user/v1/deliveries/${deliveryID}`,
      { headers: CHANNEL_HEADERS_LIVE }
    );
    return response.data.data;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

// ─── Downloads ────────────────────────────────────────────────────────
export const DELIVERY_DOCUMENT_TYPE = {
  SHIPPING_LABEL: 'SHIPPING_LABEL',
  SHIPPING_DOC: 'SHIPPING_DOC',
} as const;

export type DeliveryDocumentType =
  (typeof DELIVERY_DOCUMENT_TYPE)[keyof typeof DELIVERY_DOCUMENT_TYPE];

// Stream a blob response to the browser as a file download.
const triggerBlobDownload = (
  data: BlobPart,
  filename: string,
  mime: string
) => {
  const url = window.URL.createObjectURL(new Blob([data], { type: mime }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export interface ReconciliationParams {
  startDate?: string;
  endDate?: string;
  status?: string;
  showOutlets?: boolean;
  providerOutletIDs?: string;
}

// GET /client-user/v1/deliveries/download — reconciliation CSV for the current
// filter set (mirrors next-portal's downloadDeliveryReconciliation params).
export const downloadDeliveryReconciliation = async (
  params: ReconciliationParams = {}
): Promise<void> => {
  const response = await Axios.get('/client-user/v1/deliveries/download', {
    headers: CHANNEL_HEADERS_LIVE,
    responseType: 'blob',
    params: {
      platform: 'LINK',
      start_date: params.startDate ?? '',
      end_date: params.endDate ?? '',
      status: params.status,
      showOutlets: params.showOutlets ?? false,
      providerOutletIDs: params.providerOutletIDs ?? '',
    },
  });
  const name =
    params.startDate && params.endDate
      ? `Data Deliveries - ${params.startDate} to ${params.endDate}.csv`
      : 'All Deliveries Data.csv';
  triggerBlobDownload(response.data, name, 'text/csv');
};

// POST /client-user/v1/deliveries/documents/download — shipping label / doc PDF
// for the given deliveries (driven by the table row selection).
export const downloadShippingDocuments = async (data: {
  deliveryUIDs: string[];
  type: DeliveryDocumentType;
}): Promise<void> => {
  const response = await Axios.post(
    '/client-user/v1/deliveries/documents/download',
    data,
    { headers: CHANNEL_HEADERS_LIVE, responseType: 'blob' }
  );
  const name =
    data.deliveryUIDs.length === 1
      ? `${data.type} - ${data.deliveryUIDs[0]}.pdf`
      : `Bulk ${data.type}.pdf`;
  triggerBlobDownload(response.data, name, 'application/pdf');
};

